/**
 * Post Comments List and Form Component
 * Top-level comments with one level of nested replies.
 * Comment authors can edit/delete their own; the post author can delete any.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import VerifiedBadge from '../common/VerifiedBadge';

const parentIdOf = (c) => {
  const p = c.parentComment;
  return p ? (p._id || p).toString() : null;
};

const PostCommentsListAndForm = ({
  comments,
  isAuthenticated,
  onSubmitComment,
  currentUserId,
  postAuthorId,
  onEditComment,
  onDeleteComment,
  onLikeComment
}) => {
  const [commentText, setCommentText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    onSubmitComment(commentText);
    setCommentText('');
  };

  const startEdit = (comment) => {
    setEditingId(comment._id);
    setEditText(comment.content);
  };

  const saveEdit = (commentId) => {
    if (editText.trim()) onEditComment(commentId, editText);
    setEditingId(null);
    setEditText('');
  };

  const submitReply = (parentId) => {
    if (replyText.trim()) onSubmitComment(replyText, parentId);
    setReplyingTo(null);
    setReplyText('');
  };

  const topLevel = comments.filter((c) => !parentIdOf(c));
  const repliesFor = (id) => comments.filter((c) => parentIdOf(c) === id?.toString());

  const renderComment = (comment, isReply) => {
    const commentUserId = comment.user?._id || comment.user;
    const canEdit = currentUserId && commentUserId?.toString() === currentUserId?.toString();
    const canDelete = canEdit || (currentUserId && postAuthorId?.toString() === currentUserId?.toString());
    const likeList = comment.likes || [];
    const likedByMe = currentUserId && likeList.some(id => (id._id || id)?.toString() === currentUserId?.toString());

    return (
      <div key={comment._id} className="comment">
        <div className="comment-author">
          {comment.user?.avatar ? (
            <img src={comment.user.avatar} alt={comment.user.username} className="comment-avatar" />
          ) : (
            <div className="comment-avatar-placeholder">
              {comment.user?.username?.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div className="comment-author-name">
              {comment.user?.fullName || comment.user?.username}
              {comment.user?.isVerified && <VerifiedBadge size={13} />}
            </div>
            {editingId === comment._id ? (
              <div className="comment-edit">
                <input
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  autoFocus
                />
                <button type="button" onClick={() => saveEdit(comment._id)}>Save</button>
                <button type="button" onClick={() => setEditingId(null)}>Cancel</button>
              </div>
            ) : (
              <div className="comment-content">{comment.content}</div>
            )}
            {editingId !== comment._id && (
              <div className="comment-actions">
                {onLikeComment && (
                  <button
                    type="button"
                    className={`comment-like ${likedByMe ? 'liked' : ''}`}
                    onClick={() => onLikeComment(comment._id)}
                  >
                    <Heart size={13} fill={likedByMe ? 'currentColor' : 'none'} />
                    {likeList.length > 0 && <span>{likeList.length}</span>}
                  </button>
                )}
                {!isReply && isAuthenticated && (
                  <button
                    type="button"
                    onClick={() => { setReplyingTo(comment._id); setReplyText(''); }}
                  >
                    Reply
                  </button>
                )}
                {canEdit && (
                  <button type="button" onClick={() => startEdit(comment)}>Edit</button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="comment-delete"
                    onClick={() => onDeleteComment(comment._id)}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}

            {replyingTo === comment._id && (
              <div className="comment-edit comment-reply-form">
                <input
                  type="text"
                  placeholder="Write a reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  autoFocus
                />
                <button type="button" onClick={() => submitReply(comment._id)}>Reply</button>
                <button type="button" onClick={() => setReplyingTo(null)}>Cancel</button>
              </div>
            )}

            {!isReply && repliesFor(comment._id).length > 0 && (
              <div className="comment-replies">
                {repliesFor(comment._id).map((reply) => renderComment(reply, true))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="post-comments">
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} className="comment-form">
          <input
            type="text"
            placeholder="Write a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
          <button type="submit">Post</button>
        </form>
      ) : (
        <div className="comment-login-prompt">
          <Link to="/login" className="login-link">
            Login to comment
          </Link>
        </div>
      )}
      <div className="comments-list">
        {topLevel.map((comment) => renderComment(comment, false))}
      </div>
    </div>
  );
};

export default PostCommentsListAndForm;
